import { useTranslation } from "@sagot/i18n";
import { cssVars, ErrorState, Icon, Skeleton } from "@sagot/ui";
import type { ReactNode } from "react";
import type { IconName } from "@sagot/ui";
import { useFormatters } from "../shared/format";
import { useGapStats } from "./use-gaps";

function StatTile({ label, icon, iconClass = "text-fg-subtle", children }: { label: string; icon: IconName; iconClass?: string; children: ReactNode }) {
  return (
    <div className="card card-body">
      <div className="flex items-center justify-between">
        <span className="stat-label">{label}</span>
        <Icon name={icon} className={`size-4 ${iconClass}`} />
      </div>
      {children}
    </div>
  );
}

function Delta({ current, previous }: { current: number; previous: number }) {
  const { t } = useTranslation();
  const delta = current - previous;
  if (delta === 0) return null;
  const up = delta > 0;
  return (
    <div className={`mt-1 flex items-center gap-1 text-xs font-semibold ${up ? "text-success" : "text-fg-subtle"}`}>
      <Icon name={up ? "trending-up" : "trending-down"} className="size-3.5" />
      {t("engage.gaps.stats.vsLastMonth", { delta: `${up ? "+" : ""}${String(delta)}` })}
    </div>
  );
}

/** Open gaps · resolved this month · answer coverage · top category. */
export function GapStatsGrid() {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const stats = useGapStats();
  if (stats.isError) {
    return (
      <div className="card">
        <ErrorState headingLevel={2} title={t("engage.gaps.statsError")} onRetry={() => void stats.refetch()} retryLabel={t("common.retry")} />
      </div>
    );
  }
  if (stats.isPending) {
    return (
      <section className="grid grid-cols-2 gap-4 lg:grid-cols-4" aria-hidden="true">
        {[0, 1, 2, 3].map((n) => (
          <div key={n} className="card card-body space-y-3">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-7 w-16" />
            <Skeleton className="h-3 w-32" />
          </div>
        ))}
      </section>
    );
  }
  const s = stats.data;
  const coverage = s.answer_coverage;
  return (
    <section className="grid grid-cols-2 gap-4 lg:grid-cols-4" aria-label={t("engage.gaps.title")} data-testid="gap-stats">
      <StatTile label={t("engage.gaps.stats.open")} icon="help-circle" iconClass="text-accent">
        <div className="stat-value" data-testid="open-count">
          {fmt.number(s.open_count)}
        </div>
        <div className="mt-1 text-xs font-semibold text-fg-subtle">{t("engage.gaps.stats.questionsAffected", { count: s.questions_affected })}</div>
      </StatTile>
      <StatTile label={t("engage.gaps.stats.resolvedMonth")} icon="check-circle">
        <div className="stat-value" data-testid="resolved-count">
          {fmt.number(s.resolved_this_month)}
        </div>
        <Delta current={s.resolved_this_month} previous={s.resolved_previous_month} />
      </StatTile>
      <StatTile label={t("engage.gaps.stats.coverage")} icon="target">
        <div className="stat-value">{coverage === null ? "—" : fmt.percent(coverage)}</div>
        {coverage === null ? (
          <div className="mt-1 text-xs font-semibold text-fg-subtle">{t("engage.gaps.stats.coverageNone")}</div>
        ) : (
          <div
            role="meter"
            aria-label={t("engage.gaps.stats.coverage")}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(coverage * 100)}
            className="mt-2 h-1.5 rounded-pill bg-sunken"
          >
            <div className="h-1.5 w-[var(--geo-width)] rounded-pill bg-primary" style={cssVars({ "--geo-width": fmt.percent(coverage) })} />
          </div>
        )}
      </StatTile>
      <StatTile label={t("engage.gaps.stats.topCategory")} icon="tag">
        <div className="mt-1 text-lg font-extrabold tracking-tight">{s.top_category ?? t("engage.gaps.stats.topCategoryNone")}</div>
        <div className="mt-1 text-xs font-semibold text-fg-subtle">{t("engage.gaps.stats.topCategoryHint")}</div>
      </StatTile>
    </section>
  );
}
